import { ForwardedRef, forwardRef } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { getFragmentData, gql } from "__generated__/gql";
import {
  ProductLineSelectOptionFragment,
  ProductLineSelectOptionsQuery,
} from "__generated__/gql/graphql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";

type Option = ProductLineSelectOptionFragment;

const OPTION_FRAGMENT_STR = /* GraphQL */ `
  fragment ProductLineSelectOption on ProductLine {
    id
    name
  }
`;

const optionFragment = gql(OPTION_FRAGMENT_STR);

const valuesQuery = gql(/* GraphQL */ `
  query ProductLineSelectValue($ids: [UUID!]!) {
    productLinesList(filter: { id: { in: $ids } }) {
      ...ProductLineSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query ProductLineSelectOptions($brandId: UUID!) {
    productLinesList(
      condition: { brandId: $brandId }
      orderBy: CREATED_AT_ASC
    ) {
      ...ProductLineSelectOption
    }
  }
`);

function getOptions(data: ProductLineSelectOptionsQuery) {
  return getFragmentData(optionFragment, data?.productLinesList);
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti>;

function ProductLineSelect<IsMulti extends boolean = false>(
  { isMulti, onChange, value: rawValue, isLoading, ...rest }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valuesQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.productLinesList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const lazyQuery = useLazyQuery(optionsQuery, {
    variables: brandId ? { brandId } : undefined,
  });

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
    skip: !brandId,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder="Select a Product Line…"
      noOptionsMessage={() => "No Product Lines found"}
      {...rest}
    />
  );
}

export default forwardRef(ProductLineSelect);
